import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Spinner } from '../../components/ui/spinner';
import { formatCurrency, formatDate } from '../../lib/i18n';
import {
  ArrowLeft,
  User,
  Phone,
  Mail,
  MapPin,
  Bike,
  Plus,
  Edit,
  Star,
  Bell,
  Droplet,
  Disc,
  Link as LinkIcon,
  Filter,
  Circle,
  Zap,
  Cable,
  CircleDot,
  Layers,
  RotateCcw,
  Battery,
  Clock,
  Cog,
  Tag,
  AlertCircle,
  CreditCard,
  FileText
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  'Oils & Lubricants': Droplet, 'Brake Pads & Shoes': Disc, 'Chain & Sprocket': LinkIcon,
  'Filters': Filter, 'Tires & Tubes': Circle, 'Spark Plugs': Zap, 'Cables': Cable,
  'Rubber & Seals': CircleDot, 'Gasket Kits': Layers, 'Clutch Components': RotateCcw,
  'Electrical': Battery, 'Timing Chain': Clock, 'Bearings': Cog,
};

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-blue-100 text-blue-800',
  waiting_parts: 'bg-orange-100 text-orange-800',
  completed: 'bg-green-100 text-green-800',
  delivered: 'bg-slate-100 text-slate-800'
};

export default function CustomerDetailPage() {
  const { id, garageSlug } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [bikes, setBikes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [points, setPoints] = useState(null);
  const [upcomingReminders, setUpcomingReminders] = useState([]);
  const [customerDues, setCustomerDues] = useState(null);

  // Build base path for navigation
  const basePath = garageSlug ? `/${garageSlug}/admin` : '/admin';

  useEffect(() => {
    fetchCustomerData();
  }, [id]);

  const fetchCustomerData = async () => {
    try {
      const [customerRes, jobsRes, bikesRes] = await Promise.all([
        axios.get(`${API}/customers/${id}`),
        axios.get(`${API}/jobs?customer_id=${id}`),
        axios.get(`${API}/bikes?customer_id=${id}`)
      ]);
      setCustomer(customerRes.data);
      setJobs(jobsRes.data || []);
      setBikes(bikesRes.data || []);
      // Fetch loyalty points (non-blocking)
      axios.get(`${API}/marketing/loyalty/customers/${id}/points`).then(r => setPoints(r.data)).catch(() => {});
      // Fetch upcoming reminders (non-blocking)
      axios.get(`${API}/marketing/reminders`, { params: { customer_id: id, status: 'pending' } })
        .then(r => setUpcomingReminders(r.data?.reminders || []))
        .catch(() => {});
      // Fetch customer dues (non-blocking)
      axios.get(`${API}/invoices/dues/customer/${id}`)
        .then(r => setCustomerDues(r.data))
        .catch(() => setCustomerDues(null));
    } catch (error) {
      console.error('Failed to fetch customer:', error);
      navigate(`${basePath}/customers`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Spinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  if (!customer) return null;

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(`${basePath}/customers`)} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="w-7 h-7 text-blue-800" />
              </div>
              <div>
                <h1 className="text-2xl font-bold font-['Barlow_Condensed']">{customer.name}</h1>
                <p className="text-slate-500">{language === 'en' ? 'Customer since' : 'গ্রাহক হয়েছেন'} {formatDate(customer.created_at, language)}</p>
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            <Link to={`${basePath}/customers/${id}/edit`} data-testid="edit-customer-btn">
              <Button variant="outline" className="gap-2">
                <Edit className="w-4 h-4" />
                {language === 'en' ? 'Edit' : 'সম্পাদনা'}
              </Button>
            </Link>
            <Link to={`${basePath}/jobs/new?customer_id=${id}`} data-testid="new-job-for-customer">
              <Button className="bg-blue-800 hover:bg-blue-900">
                <Plus className="w-4 h-4 mr-2" />
                {t('new_job')}
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-800">{customer.total_jobs || jobs.length}</p>
              <p className="text-sm text-slate-500">{t('total_jobs')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{formatCurrency(customer.total_spent, language)}</p>
              <p className="text-sm text-slate-500">{t('total_spent')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-500">{bikes.length}</p>
              <p className="text-sm text-slate-500">{t('bikes_owned')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-slate-600">
                {jobs.filter(j => j.status !== 'delivered').length}
              </p>
              <p className="text-sm text-slate-500">{language === 'en' ? 'Active Jobs' : 'সক্রিয় জব'}</p>
            </CardContent>
          </Card>
        </div>

        {/* Loyalty Points Card */}
        {points && points.current_balance !== undefined && (
          <Card className="mb-6 border-yellow-200 bg-yellow-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Star className="w-6 h-6 text-yellow-600" />
                  <div>
                    <p className="font-semibold text-yellow-800">{t('marketing_loyalty_points')}</p>
                    <p className="text-sm text-yellow-700">
                      {language === 'en' ? 'Earned' : 'অর্জিত'}: {points.total_earned || 0} • {language === 'en' ? 'Redeemed' : 'রিডিম'}: {points.total_redeemed || 0}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-yellow-700">{points.current_balance}</p>
                  <p className="text-xs text-yellow-600">{language === 'en' ? 'points' : 'পয়েন্ট'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Outstanding Dues + Upcoming Reminders — side-by-side grid */}
        {(customerDues?.total_due > 0 || upcomingReminders.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Outstanding Dues */}
            {customerDues && customerDues.total_due > 0 && (
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-red-600" />
                      <p className="font-semibold text-red-800">{language === 'en' ? 'Outstanding Dues' : 'বকেয়া পরিশোধ'}</p>
                    </div>
                    <Link to={`${basePath}/invoices/dues?customer=${encodeURIComponent(customer.name)}`}>
                      <Button size="sm" variant="outline" className="h-7 text-xs text-red-700 border-red-300 hover:bg-red-100">
                        <CreditCard className="w-3 h-3 mr-1" />
                        {language === 'en' ? 'Manage' : 'পরিচালনা'}
                      </Button>
                    </Link>
                  </div>
                  <p className="text-2xl font-bold text-red-700 mb-2">{formatCurrency(customerDues.total_due, language)}
                    <span className="text-xs font-normal text-red-500 ml-2">({customerDues.total_invoices} {language === 'en' ? 'invoices' : 'ইনভয়েস'})</span>
                  </p>
                  <div className="space-y-1.5">
                    {customerDues.invoices.slice(0, 3).map((inv) => (
                      <Link key={inv.id} to={`${basePath}/invoices/${inv.id}`}>
                        <div className="flex items-center justify-between py-1.5 px-2 bg-white rounded border text-sm hover:bg-slate-50 transition-colors">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-blue-700">{inv.invoice_number}</span>
                            <Badge className={`text-[10px] px-1.5 py-0 leading-4 ${inv.status === 'partial' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                              {inv.status === 'partial' ? (language === 'en' ? 'Partial' : 'আংশিক') : (language === 'en' ? 'Unpaid' : 'অপরিশোধিত')}
                            </Badge>
                          </div>
                          <span className="font-bold text-red-700">{formatCurrency(inv.due_amount, language)}</span>
                        </div>
                      </Link>
                    ))}
                    {customerDues.invoices.length > 3 && (
                      <p className="text-xs text-red-600 text-center pt-1">
                        +{customerDues.invoices.length - 3} {language === 'en' ? 'more' : 'আরও'}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Upcoming Reminders */}
            {upcomingReminders.length > 0 && (
              <Card className="border-orange-200 bg-orange-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Bell className="w-5 h-5 text-orange-600" />
                    <p className="font-semibold text-orange-800">{language === 'en' ? 'Upcoming Reminders' : 'আসন্ন রিমাইন্ডার'}</p>
                  </div>
                  <div className="space-y-2">
                    {upcomingReminders.slice(0, 3).map((rem) => {
                      const IconComp = CATEGORY_ICONS[rem.consumable_category_name] || Tag;
                      return (
                        <div key={rem.id} className="flex items-center gap-2 py-1.5 px-2 bg-white rounded border text-sm">
                          <IconComp className="w-4 h-4 text-blue-800 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-900 truncate">{rem.consumable_category_name}</p>
                            <p className="text-xs text-slate-500 truncate">{rem.target_name}</p>
                          </div>
                          <p className="text-xs text-orange-700 font-medium whitespace-nowrap">
                            {formatDate(rem.remind_date, language)}
                          </p>
                        </div>
                      );
                    })}
                    {upcomingReminders.length > 3 && (
                      <p className="text-xs text-orange-600 text-center pt-1">
                        +{upcomingReminders.length - 3} {language === 'en' ? 'more' : 'আরও'}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Contact Info */}
          <Card>
            <CardHeader>
              <CardTitle>{t('customer_details')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">{t('phone')}</p>
                  <p className="font-medium">{customer.phone}</p>
                  {customer.additional_phones?.length > 0 && (
                    <div className="mt-1 space-y-0.5">
                      {customer.additional_phones.map((p, i) => (
                        <p key={i} className="text-sm text-slate-600">{p}</p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {customer.email && (
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">{t('email')}</p>
                    <p className="font-medium">{customer.email}</p>
                  </div>
                </div>
              )}
              {customer.address && (
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">{t('address')}</p>
                    <p className="font-medium">{customer.address}</p>
                  </div>
                </div>
              )}
              {customer.notes && (
                <div className="pt-4 border-t">
                  <p className="text-sm text-slate-500 mb-1">{t('notes')}</p>
                  <p className="text-slate-600">{customer.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Bikes */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bike className="w-5 h-5" />
                {t('bikes_owned')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {bikes.length > 0 ? (
                <div className="space-y-3">
                  {bikes.map((bike) => (
                    <div key={bike.id} className="p-3 bg-slate-50 rounded-lg" data-testid={`bike-${bike.id}`}>
                      <p className="font-medium">{bike.make} {bike.model}</p>
                      <div className="flex gap-2 text-sm text-slate-500">
                        {bike.year && <span>{bike.year}</span>}
                        {bike.registration_number && <span>{bike.year ? '•' : ''} {bike.registration_number}</span>}
                        {bike.color && <span>{(bike.year || bike.registration_number) ? '•' : ''} {bike.color}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">
                  {language === 'en' ? 'No vehicles registered' : 'কোন বাইক নিবন্ধিত নেই'}
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Service History */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>{t('service_history')}</CardTitle>
          </CardHeader>
          <CardContent>
            {jobs.length > 0 ? (
              <div className="space-y-3">
                {jobs.map((job) => (
                  <Link key={job.id} to={`${basePath}/jobs/${job.id}`} data-testid={`job-history-${job.id}`}>
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-blue-800">{job.job_number}</span>
                          <Badge className={statusColors[job.status]}>{job.status?.replace('_', ' ')}</Badge>
                        </div>
                        <p className="text-sm text-slate-500">
                          {job.bike_model} • {formatDate(job.created_at, language)}
                        </p>
                      </div>
                      <p className="font-bold">{formatCurrency(job.total_amount, language)}</p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">
                {language === 'en' ? 'No service history yet' : 'এখনো কোন সার্ভিস ইতিহাস নেই'}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
